import { hasScope, type ApiClient } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { client } from '../../session';

/**
 * The index advisor (M24, `docs/index-advisor.md`, `IndexAdvisor/IndexAdvisorEndpoints.cs`): the AlloyDB / Cloud SQL
 * index advisor's recommendations per database, fleet scans that count them across tenant databases, the default set
 * they are checked against, a model's review (Vertex AI) and the resulting `index-additions.sql` blocks. The hub never runs DDL.
 */

/** How a recommended index relates to the default set (template + additions). */
export type Coverage = 'new' | 'in_default' | 'covered' | 'extends' | 'unparsed';

export interface AdviceStatement {
  query: string;
  calls: number | null;
  totalMs: number | null;
}

export interface TableStats {
  rows: number;
  totalBytes: number;
  inserts: number;
  updates: number;
  deletes: number;
  seqScans: number;
  idxScans: number;
}

export interface ExistingIndex {
  name: string;
  def: string;
  scans: number;
}

export interface AdviceRecommendation {
  id: string;
  /** The advisor's text as it came. */
  recommended: string;
  key: string;
  /** `schema.table`; null when the advisor's text could not be parsed. */
  table: string | null;
  ddl: string | null;
  coverage: Coverage;
  /** The default-set index that covers or is extended (by name). */
  coveredBy: string | null;
  statements: AdviceStatement[];
}

export interface IndexAdvice {
  status: 'ok' | 'no_advisor';
  note: string | null;
  /** Has `tbe_client` and `tbe_warehouse` (scans only count tenant databases). */
  tenant: boolean;
  defaultSetError: string | null;
  recommendations: AdviceRecommendation[];
  tables: Record<string, TableStats>;
  indexes: Record<string, ExistingIndex[]>;
}

export interface IndexAdvisorSettings {
  templateServerId: string | null;
  templateDatabase: string | null;
  additionsSql: string;
  updatedAt: string | null;
  updatedBy: string | null;
  review: { configured: boolean; model: string };
}

export interface SettingsInput {
  templateServerId: string | null;
  templateDatabase: string | null;
  additionsSql: string;
}

export type IndexClass = 'general' | 'conditional';

export interface DefaultSetIndex {
  source: 'template' | 'additions';
  name: string;
  sql: string;
  key: string | null;
  table: string | null;
  class: IndexClass | null;
  when: string | null;
  why: string | null;
  cost: string | null;
}

export interface DefaultSet {
  templateServerId: string | null;
  templateDatabase: string | null;
  error: string | null;
  indexes: DefaultSetIndex[];
}

export type ScanStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
export type ReviewStatus = 'none' | 'queued' | 'running' | 'succeeded' | 'failed';

export interface ScanReview {
  status: ReviewStatus;
  spec: { minDatabases?: number; maxTables?: number } | null;
  by: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  model: string | null;
  usage: { inputTokens: number; outputTokens: number; tables: number } | null;
  error: string | null;
}

export interface Scan {
  id: number;
  status: ScanStatus;
  /** Empty = every server. */
  serverIds: string[];
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  cancelRequested: boolean;
  databasesTotal: number;
  databasesDone: number;
  tenants: number;
  withAdvisor: number;
  error: string | null;
  review: ScanReview;
}

export interface ScanDatabase {
  serverId: string;
  /** '' = the server's database list failed. */
  database: string;
  status: 'ok' | 'no_advisor' | 'error';
  error: string | null;
  candidates: number;
  /** Read on a server in recovery (a read replica). */
  standby?: boolean;
  /** A replica's copy: the server whose copy of this database counts (the primary). */
  replicaOf?: string | null;
}

export type Verdict = 'add' | 'widen' | 'reorder' | 'covered' | 'conditional' | 'skip';

export interface CandidateReview {
  id: string;
  verdict: Verdict;
  ddl: string | null;
  class: IndexClass | 'none';
  when: string | null;
  why: string | null;
  cost: string | null;
  confidence: 'low' | 'medium' | 'high';
  model: string;
  at: string;
  /** The proposed DDL did not parse; it is not used in the recommendation. */
  ddlInvalid?: true;
}

export interface CandidateEvidence {
  withAdvisor: number;
  /** databases / withAdvisor, 0–1. */
  share: number;
  statements: { query: string; databases: number; calls: number | null; totalMs: number | null }[];
  table: {
    rowsMedian: number;
    rowsMax: number;
    bytesMax: number;
    insertsMedian: number;
    updatesMedian: number;
    deletesMedian: number;
    seqScansMedian: number;
    idxScansMedian: number;
  } | null;
  existingIndexes: { def: string; name: string; databases: number; scansMedian: number }[];
}

export interface Candidate {
  id: string;
  key: string;
  tableName: string;
  ddl: string;
  databases: number;
  coverage: Coverage;
  coveredBy: string | null;
  evidence: CandidateEvidence;
  /** null = not reviewed; `{error}` = the review of its table failed. */
  review: CandidateReview | { error: string; model?: string } | null;
}

export interface CandidateHit {
  candidateId: string;
  serverId: string;
  database: string;
  recommended: string;
  statements: AdviceStatement[];
  tableStats: TableStats | null;
  tableIndexes: ExistingIndex[];
  /** A replica's copy: the server whose copy of this database counts. */
  replicaOf?: string | null;
}

export interface Proposal {
  candidateId: string;
  class: IndexClass;
  when: string | null;
  why: string | null;
  cost: string | null;
  ddl: string;
  table: string;
  databases: number;
  source: string;
}

export interface Recommendation {
  scan: Scan;
  reviewed: boolean;
  proposals: Proposal[];
  /** `index-additions.sql` blocks. */
  sql: string;
}

export interface ScriptOptions {
  /** Add the proposals of this scan (`latest` = newest succeeded). */
  scanId?: number | 'latest';
  /** Include conditional additions too. */
  conditional?: boolean;
  minDatabases?: number;
}

/** The review's verdict when there is one (not an error). */
export const reviewOf = (c: Pick<Candidate, 'review'>): CandidateReview | null =>
  c.review && 'verdict' in c.review ? c.review : null;

export const reviewError = (c: Pick<Candidate, 'review'>): string | null =>
  c.review && 'error' in c.review ? c.review.error : null;

export const isScanActive = (s: Pick<Scan, 'status'> | null | undefined) =>
  s?.status === 'queued' || s?.status === 'running';

export const isReviewActive = (s: Pick<Scan, 'review'> | null | undefined) =>
  s?.review.status === 'queued' || s?.review.status === 'running';

const seg = (id: string | number) => encodeURIComponent(String(id));

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function indexAdvisorApi(c: ApiClient) {
  const dbPath = (serverId: string, database: string) =>
    `/db/servers/${seg(serverId)}/databases/${seg(database)}`;
  return {
    advice: (serverId: string, database: string) =>
      c.get<IndexAdvice>(`${dbPath(serverId, database)}/index-advice`),
    /** The `CREATE INDEX CONCURRENTLY` script for one database (text). */
    script: async (serverId: string, database: string, o: ScriptOptions = {}) => {
      const r = await c.requestRaw(
        'GET',
        `${dbPath(serverId, database)}/index-script${query({
          scanId: o.scanId,
          conditional: o.conditional,
          minDatabases: o.minDatabases,
        })}`,
        undefined,
        { accept: 'text/plain' }
      );
      return r.text();
    },
    settings: () => c.get<IndexAdvisorSettings>('/db/index-advisor/settings'),
    saveSettings: (body: SettingsInput) =>
      c.put<IndexAdvisorSettings & { additions: number }>('/db/index-advisor/settings', body),
    defaultSet: () => c.get<DefaultSet>('/db/index-advisor/default-set'),
    scans: (limit = 20) => c.get<Scan[]>(`/db/index-scans${query({ limit })}`),
    startScan: (serverIds: string[] = []) => c.post<Scan>('/db/index-scans', { serverIds }),
    scan: (scanId: number | 'latest') =>
      c.get<{ scan: Scan; databases: ScanDatabase[] }>(`/db/index-scans/${seg(scanId)}`),
    cancelScan: (scanId: number) => c.post<Scan>(`/db/index-scans/${seg(scanId)}/cancel`),
    candidates: (scanId: number | 'latest') =>
      c.get<{ scan: Scan; candidates: Candidate[] }>(`/db/index-scans/${seg(scanId)}/candidates`),
    candidate: (scanId: number | 'latest', candidateId: string) =>
      c.get<{ scan: Scan; candidate: Candidate; hits: CandidateHit[] }>(
        `/db/index-scans/${seg(scanId)}/candidates/${seg(candidateId)}`
      ),
    review: (scanId: number, body: { minDatabases: number; maxTables: number }) =>
      c.post<Scan>(`/db/index-scans/${seg(scanId)}/review`, body),
    recommendation: (scanId: number | 'latest', minDatabases?: number) =>
      c.get<Recommendation>(`/db/index-scans/${seg(scanId)}/recommendation${query({ minDatabases })}`),
  };
}

export type IndexAdvisorApi = ReturnType<typeof indexAdvisorApi>;

export const indexAdvisor = indexAdvisorApi(client);

/** May the caller start scans and reviews and edit the default set (`ophub:db:advise`). */
export const canAdvise = (scopes: readonly string[] | null | undefined) => hasScope(scopes, SCOPES.dbAdvise);

/** The fleet page and a candidate's page. */
export const ADVISOR_PATH = '/databases/index-advisor';
export const candidatePath = (scanId: number | string, candidateId: string) =>
  `${ADVISOR_PATH}/${seg(scanId)}/${seg(candidateId)}`;
